import React, { InputHTMLAttributes, forwardRef } from 'react';
import { cn, hashCode } from '@express-labs/raven-tools';
import s from './FloatingInput.scss';
import { WrappedFieldMetaProps } from 'redux-form';

type CommonFloatingInputProps = {
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  errorClassName?: string;
  errors?: string[];
  helperClassName?: string;
  helpText?: string | React.ReactNode;
  id?: string;
  inputClassName?: string;
  label: string;
  labelClassName?: string;
  maxLength?: number;
  meta?: MetaType | WrappedFieldMetaProps;
  name?: string;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  textCount?: boolean;
  type?: string;
  value?: string;
};

export type FloatingInputProps = CommonFloatingInputProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, keyof CommonFloatingInputProps>;

const FloatingInput = forwardRef<HTMLInputElement, FloatingInputProps>(
  function FloatingInput(props, ref) {
    const {
      children,
      className,
      disabled = false,
      errorClassName,
      errors = [],
      helperClassName,
      helpText,
      id,
      inputClassName,
      label,
      labelClassName,
      maxLength = 100,
      meta,
      name,
      onBlur,
      onChange,
      required = false,
      textCount = false,
      type = 'text',
      value,
      ...passThroughProps
    } = props;

    const hasError = Array.isArray(errors) && errors.length;
    const errorMeta = meta?.error;
    const touchedMeta = meta?.touched;
    const valueLength = value ? value.length : 0;
    const errorLength = errors.length;

    const renderError = () => {
      if (errorLength > 1) {
        return (
          <ul className={s.errorTextList}>
            {errors?.map((error) => <li key={hashCode(error)}>{error}</li>)}
          </ul>
        );
      }
      return errors?.[0];
    };

    return (
      <div className={cn(s.floatingInput, className)}>
        <div className={cn(s.inputFieldHolder, disabled && s.disabled)}>
          <input
            data-testid="input"
            {...passThroughProps}
            className={cn(
              s.inputField,
              inputClassName,
              ((errorMeta && touchedMeta) || (hasError && !errorMeta)) &&
                s.error
            )}
            disabled={disabled}
            id={id}
            required={required}
            type={type}
            maxLength={maxLength}
            name={name}
            onChange={onChange}
            onBlur={onBlur}
            ref={ref}
            value={value}
          />
          <label
            className={cn(
              s.floatingLabel,
              !!value && s.floatingLabelActive,
              required && !disabled && s.floatingLabelRequired,
              labelClassName
            )}
            data-testid="label"
            htmlFor={id}
          >
            {label}
          </label>
        </div>
        {!errorMeta && !hasError ? (
          <div className={s.helpHolder}>
            <div className={cn(s.helpText, helperClassName)}>{helpText}</div>
            {textCount && (
              <div className={s.helpCount}>{`${valueLength}/${maxLength}`}</div>
            )}
          </div>
        ) : null}
        {/* Handling errors that comes from errors array prop */}
        {hasError && !errorMeta ? (
          <div className={cn(s.errorText, errorClassName)}>{renderError()}</div>
        ) : null}
        {/* Handling errors that comes from redux-form */}
        {errorMeta && touchedMeta && (
          <div className={cn(s.errorText, errorClassName)}>{meta.error}</div>
        )}
        {children}
      </div>
    );
  }
);

FloatingInput.displayName = 'FloatingInput';

export default FloatingInput;
